import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * GrownupPinGate — a lightweight kid-safety check that fronts the
 * "For Grown-ups" section and the admin-only routes.
 *
 * Two modes:
 *   • `mode="math"` (default) — a small multiplication problem
 *     ("7 × 8 = ?"). Used to guard general parent / teacher pages so
 *     a pre-reader can't wander in but a parent unlocks in one tap.
 *   • `mode="pin"` — a fixed 6-digit PIN ("160986") supplied by the
 *     product owner. Used to guard the sensitive admin routes
 *     (Lesson Admin · Question Bank Admin).
 *
 * Once passed in a session, each mode is remembered in sessionStorage
 * so a grown-up can move between related pages without re-solving. A
 * new browser session (or tab close) re-locks. `resetGrownupGate()` is
 * exposed for a future "sign out" affordance if we ever want it.
 */
const MATH_SESSION_KEY  = "we_grownup_gate_passed_v1";
const ADMIN_SESSION_KEY = "we_admin_gate_passed_v1";
const ADMIN_PIN         = "160986"; // product-owner supplied

function generatePuzzle() {
  // Pick two single-digit factors ≥ 3 so the problem is beyond a 5-yr
  // old's typical fingers-and-toes range but trivial for an adult.
  const a = 3 + Math.floor(Math.random() * 7); // 3..9
  const b = 3 + Math.floor(Math.random() * 7); // 3..9
  return { a, b, answer: a * b };
}

function sessionKeyFor(mode) {
  return mode === "pin" ? ADMIN_SESSION_KEY : MATH_SESSION_KEY;
}

export function isGrownupGatePassed(mode = "math") {
  try { return sessionStorage.getItem(sessionKeyFor(mode)) === "1"; } catch { return false; }
}

export function markGrownupGatePassed(mode = "math") {
  try { sessionStorage.setItem(sessionKeyFor(mode), "1"); } catch {/* noop */}
}

export function resetGrownupGate(mode) {
  try {
    if (mode) sessionStorage.removeItem(sessionKeyFor(mode));
    else {
      sessionStorage.removeItem(MATH_SESSION_KEY);
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
  } catch {/* noop */}
}

/**
 * Renders an in-page overlay puzzle / PIN prompt. The parent controls
 * open/closed state; `onSuccess` runs after a correct answer and marks
 * the session as unlocked. `onCancel` closes without unlocking.
 */
export default function GrownupPinGate({ open, mode = "math", onSuccess, onCancel }) {
  const [puzzle, setPuzzle] = useState(() => generatePuzzle());
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const isPin = mode === "pin";
  const q = useMemo(() => `${puzzle.a} × ${puzzle.b} = ?`, [puzzle]);

  useEffect(() => {
    if (!open) return;
    setPuzzle(generatePuzzle());
    setValue("");
    setError("");
    // Focus input on next tick so the modal enter animation completes.
    const id = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [open, mode]);

  const submit = (e) => {
    e.preventDefault();
    if (isPin) {
      if (value.trim() === ADMIN_PIN) {
        markGrownupGatePassed("pin");
        onSuccess?.();
      } else {
        setError("PIN incorrect.");
        setValue("");
      }
      return;
    }
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) {
      setError("Please type a number.");
      return;
    }
    if (n === puzzle.answer) {
      markGrownupGatePassed("math");
      onSuccess?.();
    } else {
      setError("Not quite — try again.");
      setPuzzle(generatePuzzle());
      setValue("");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-4"
          onClick={onCancel}
          data-testid="grownup-gate-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="grownup-gate-title"
            className="relative w-full max-w-sm rounded-3xl border-4 border-slate-900 bg-white shadow-[10px_10px_0_0_rgba(15,23,42,1)] p-5"
            data-testid={isPin ? "admin-gate-modal" : "grownup-gate-modal"}
          >
            <button
              type="button"
              onClick={onCancel}
              data-testid="grownup-gate-close"
              aria-label="Close"
              className="absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)]"
            >
              <X size={16} strokeWidth={3} />
            </button>

            <div className="flex items-center gap-2 pr-8">
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-full border-[3px] border-slate-900 bg-slate-100 shadow-[0_2px_0_0_rgba(15,23,42,1)]">
                <ShieldCheck size={18} strokeWidth={3} className="text-slate-900" />
              </span>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {isPin ? "Admin PIN" : "Grown-up check"}
                </p>
                <h2 id="grownup-gate-title" className="font-heading text-xl font-bold leading-tight">
                  {isPin ? "Admin area" : "For grown-ups only"}
                </h2>
              </div>
            </div>

            <p className="mt-3 text-sm font-bold text-slate-600">
              {isPin
                ? "Enter the 6-digit admin PIN to continue."
                : "Solve this quickly to enter parent & teacher tools."}
            </p>

            <form onSubmit={submit} className="mt-4">
              {!isPin && (
                <div
                  data-testid="grownup-gate-question"
                  className="rounded-2xl border-[3px] border-slate-900 bg-yellow-100 p-4 text-center shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                >
                  <p className="font-heading text-3xl font-bold tabular-nums">{q}</p>
                </div>
              )}
              <input
                ref={inputRef}
                type={isPin ? "password" : "number"}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={value}
                onChange={(e) => { setValue(e.target.value); setError(""); }}
                data-testid={isPin ? "admin-gate-input" : "grownup-gate-input"}
                aria-label={isPin ? "Admin PIN" : "Your answer"}
                placeholder={isPin ? "Enter PIN…" : "Type answer…"}
                maxLength={isPin ? 6 : undefined}
                className={cn(
                  "w-full h-12 rounded-2xl border-[3px] border-slate-900 bg-white px-4 font-heading font-bold text-2xl tabular-nums text-center outline-none focus:bg-yellow-50",
                  isPin ? "mt-0 tracking-[0.4em]" : "mt-3",
                )}
              />
              {error && (
                <p
                  data-testid="grownup-gate-error"
                  className="mt-2 text-xs font-bold text-rose-700 text-center"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid={isPin ? "admin-gate-submit" : "grownup-gate-submit"}
                className={cn(
                  "mt-3 w-full h-12 rounded-2xl border-[3px] border-slate-900 bg-emerald-300 font-heading font-bold text-lg shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 active:translate-y-0.5 transition-all",
                )}
              >
                Unlock
              </button>
            </form>

            <p className="mt-3 text-[11px] font-bold text-slate-500 text-center">
              🛡️ Kid-safety check · No account or data required
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
